<template>
  <div class="resources-editor-contracts">
    <inline-loader :loading="loading" v-if="loading" />
    <div class="resources-editor-contracts" v-else>
      <div
        class="resources-editor-contracts__block"
        v-for="(values, category) in documents"
        :key="category"
      >
        <collapse accordion>
          <panel
            v-for="group in documentsGroups"
            :key="group.name"
            :name="group.name"
          >
            {{ group.name }}

            <span :class="getGroupCountClass(values, group.children)">
              ({{ getGroupChildren(values, group.children).length }})
            </span>

            <div slot="content">
              <collapse accordion>
                <panel
                  v-for="(typeDocuments, type) in getGroupChildren(
                    values,
                    group.children
                  ).children"
                  :key="type"
                  :name="type"
                >
                  {{ type }}
                  <span :class="getCountClass(typeDocuments, type)">
                    ({{ typeDocuments.length }})
                  </span>
                  <div slot="content">
                    <Upload
                      multiple
                      type="drag"
                      :action="uploadURL"
                      :headers="uploadHeaders"
                      :ref="getRefName(category, type)"
                      :format="getFileExtensions(category, type)"
                      :on-success="
                        response => onUploadSuccess(category, type, response)
                      "
                      :on-format-error="onFormatError"
                      :on-error="onUploadError"
                      :default-file-list="typeDocuments || []"
                      :show-upload-list="false"
                    >
                      <div style="padding: 8px 0">
                        <icon
                          type="ios-cloud-upload"
                          size="32"
                          style="color: #4484ce"
                        />
                        <p style="font-size: 12px">
                          Перетащите необходимые файлы для загрузки
                        </p>
                      </div>
                    </Upload>
                    <resource-upload-list
                      style="margin-top: 8px"
                      :list="lists[getRefName(category, type)]"
                      @handle-click="handleFileClick"
                      @handle-remove="handleFileRemove"
                    />
                  </div>
                </panel>
              </collapse>
            </div>
          </panel>
        </collapse>
      </div>
    </div>

    <modal v-model="deleteModal" width="360">
      <p slot="header" style="color: #f60; text-align: center">
        <icon type="ios-information-circle" />
        <span style="margin-left: 4px">Удаление материалов</span>
      </p>
      <div style="text-align: center">
        <p class="ms-text">Вы уверены, что хотите удалить данный документ?</p>
      </div>
      <div slot="footer">
        <i-button
          type="error"
          size="default"
          long
          :loading="deleteLoading"
          @click="deleteFile"
        >
          Удалить
        </i-button>
      </div>
    </modal>
  </div>
</template>

<script>
import InlineLoader from '@/components/loaders/inline-loader'
import ResourceUploadList from './resource-upload-list'
import { downloadFile } from '@/utils'
import { mainUrl } from '@/config/urls'

const documentsGroups = [
  {
    name: 'Договор',
    children: ['Договор (рабочий формат)', 'Договор (сканированный вид)']
  },
  {
    name: 'Календарный план',
    children: [
      'Календарный план (рабочий формат)',
      'Календарный план (сканированный вид)'
    ]
  },
  {
    name: 'Техническое задание',
    children: [
      'Техническое задание (рабочий формат)',
      'Техническое задание (сканированный вид)'
    ]
  },
  {
    name: 'Программа работ',
    children: [
      'Программа работ (рабочий формат)',
      'Программа работ (сканированный вид)'
    ]
  },
  {
    name: 'Сметы',
    children: ['Сметы (рабочий формат)', 'Сметы (сканированный вид)']
  },
  { name: 'Спец. счет', children: ['Спец. счет'] },
  {
    name: 'Прочее',
    children: ['Прочее (рабочий формат)', 'Прочее (сканированный вид)']
  }
]

export default {
  components: {
    InlineLoader,
    ResourceUploadList
  },
  data () {
    return {
      id: null,
      loading: false,
      documents: null,
      token: this.$store.state.auth.token,
      downloadUrl: `${mainUrl}/api/download_resource`,
      uploadURL: `${mainUrl}/api/upload_resource`,
      uploadHeaders: { token: this.$store.state.auth.token },
      fileTypes: [],
      lists: {},
      type: 'contracts',
      deleteModal: false,
      deleteLoading: false,
      deletingId: null
    }
  },
  computed: {
    documentsGroups () {
      const { current } = this.$store.state.contracts

      if (current.spec_account) {
        return documentsGroups
      }

      return documentsGroups.filter(g => g.name !== 'Спец. счет')
    },
    mandatoryFileTypes () {
      const { current } = this.$store.state.contracts

      if (!current) return []

      const { mandatory_file_types } = current

      if (!mandatory_file_types) return []

      return mandatory_file_types.map(({ file_type_id }) => file_type_id)
    }
  },
  watch: {
    '$route.params.id': function () {
      this.getData()
    },
    '$route.query.amendment_id': function (value) {
      this.getData()
    }
  },
  methods: {
    getGroupChildren (values, ids) {
      const children = ids.reduce((prev, el) => {
        prev[el] = values[el]

        return prev
      }, {})
      const length = Object.values(children).reduce(
        (prev, el) => prev + el.length,
        0
      )

      return {
        children,
        length
      }
    },
    getGroupCountClass (values, ids) {
      const { children } = this.getGroupChildren(values, ids)

      return Object.entries(children).some(([key, value]) => {
        const { id } = this.fileTypes.find(({ name }) => name === key)

        return !value.length && this.mandatoryFileTypes.includes(id)
      })
        ? 'ms-danger'
        : 'ms-help'
    },
    getCountClass (typeDocuments, type) {
      const { id } = this.fileTypes.find(({ name }) => name === type)

      return !typeDocuments.length && this.mandatoryFileTypes.includes(id)
        ? 'ms-danger'
        : 'ms-help'
    },
    getRefName (category, type) {
      return `${category}_${type}`
    },
    getFileExtensions (category, type) {
      const fileType = this.fileTypes.find(
        t => t.category === category && t.name === type
      )

      return fileType.extensions
    },
    handleFileClick (resource_id) {
      const url = `${this.downloadUrl}/${resource_id}?access_token=${
        this.token
      }`
      downloadFile(url)
    },
    handleFileRemove (file) {
      this.deletingId = file.id
      this.deleteModal = true
    },
    async deleteFile () {
      const id = this.deletingId

      try {
        this.deleteLoading = true
        const url = `page/document?object_id=${this.id}&object_type=${
          this.type
        }`
        const data = [{ id, disabled: true }]
        await this.$store.dispatch('POST_REQUEST', { url, data })
        await this.getData(true)
        this.deletingId = null
        this.deleteModal = false
        this.deleteLoading = false
      } catch (error) {
        console.log(error)
        this.deleteLoading = false
      }
    },
    async getData (noloading) {
      const { id } = this.$route.params
      const { amendment_id } = this.$route.query

      try {
        if (!noloading) this.loading = true
        let contractsUrl = `page/document?object_id=${id}&object_type=contracts`
        if (amendment_id) contractsUrl += `&amendment_id=${amendment_id}`

        const typesUrl = `objectInfo/file_types`
        const files = await this.$store.dispatch('GET_REQUEST', {
          url: contractsUrl
        })
        this.patchData(files)
        this.documents = files
        const data = await this.$store.dispatch('GET_REQUEST', {
          url: typesUrl
        })
        this.fileTypes = data.filter(t => t.object_name === this.type)
        setTimeout(() => this.createRefs(), 100)
        this.loading = false
      } catch (error) {
        console.log(error)
        this.loading = false
      }
    },
    patchData (documents) {
      for (const category in documents) {
        for (const type in documents[category]) {
          documents[category][type] = documents[category][type].map(f => ({
            ...f,
            name: f.file_name
          }))
        }
      }
    },
    async onUploadSuccess (category, type, response) {
      const [resource_id] = response
      const file_type = this.fileTypes.find(
        t => t.category === category && t.name === type
      )
      const file_type_id = file_type.id
      const data = [
        {
          file_type_id,
          resource_id,
          object_type: this.type
        }
      ]

      try {
        const { amendment_id } = this.$route.query

        let url = `page/document?object_id=${this.id}&object_type=${this.type}`
        if (amendment_id) url += `&amendment_id=${amendment_id}`

        await this.$store.dispatch('POST_REQUEST', { url, data })
        this.$Message.success('Файл успешно загружен')
      } catch (error) {
        console.log(error)
        this.onUploadError()
      }

      await this.getData(true)
    },
    onUploadError () {
      this.$Message.error('Произошла ошибка при загрузке файла')
    },
    onFormatError () {
      this.$Message.error('Некорректный формат документа')
    },
    createRefs () {
      for (const category in this.documents) {
        for (const type in this.documents[category]) {
          const name = this.getRefName(category, type)
          if (!this.$refs[name]) continue
          this.$set(this.lists, name, this.$refs[name][0].fileList)
        }
      }
    }
  },
  created () {
    this.id = this.$route.params.id
    this.getData()
  }
}
</script>

<style lang="less">
.resources-editor-contracts {
  font-size: 12px;

  .ivu-collapse {
    & > .ivu-collapse-item > .ivu-collapse-header {
      height: 32px;
      line-height: 32px;
    }

    &-content {
      padding: 0 8px;

      & > .ivu-collapse-content-box {
        padding-top: 8px;
        padding-bottom: 8px;
      }
    }

    .ivu-collapse {
      .ivu-collapse-header {
        height: 24px !important;
        line-height: 24px !important;
      }
    }
  }

  &__block {
    &:first-child {
      .ivu-divider {
        margin-top: 8px;
      }
    }
  }
}
</style>
